// Det som widgeten behöver veta om byrån innan kunden börjar fylla i (ADR 0052).
// Publikt, så bara sådant som redan syns på byråns webbplats.
import { z } from 'zod';

/** Vad formuläret frågar efter. Byrån väljer i kontot (ADR 0070). */
export const widgetFormSchema = z.object({
  /** Valet "Jag har en fråga". Behövs när formuläret ersätter kontaktformuläret. */
  showQuestion: z.boolean(),
  /** Ett valfritt fält för organisationsnummer i sista steget. */
  askOrgNumber: z.boolean(),
  /** Frågan om antal leverantörsfakturor. Utan den uppskattar AI antalet. */
  askInvoicesIn: z.boolean(),
});

export type WidgetForm = z.infer<typeof widgetFormSchema>;

export const DEFAULT_WIDGET_FORM: WidgetForm = {
  showQuestion: true,
  askOrgNumber: false,
  askInvoicesIn: true,
};

/** Formulärets val som de är sparade, med standardvärden för det som saknas. */
export function widgetFormFrom(sparat: unknown): WidgetForm {
  const delar = widgetFormSchema.partial().safeParse(sparat);
  const satta = delar.success
    ? Object.fromEntries(Object.entries(delar.data).filter(([, v]) => v !== undefined))
    : {};
  return { ...DEFAULT_WIDGET_FORM, ...satta };
}

export const widgetConfigSchema = z.object({
  publicId: z.string(),
  /** Byråns namn, i samtycket och rubrikerna. */
  firmName: z.string(),
  /** Byråns accentfärg som #RRGGBB. */
  accentColor: z.string().regex(/^#[0-9A-F]{6}$/),
  /** Byråns integritetspolicy. Null om byrån inte har angett någon. */
  privacyUrl: z.url().nullable(),
  /**
   * Byråns utskicksläge. Vid direkt utskick lovar formuläret att offerten
   * visas direkt, annars att den kommer med e-post.
   */
  dispatchMode: z.enum(['direct', 'delayed', 'approve_first']),
  /** Byråns kontaktperson, som står i bekräftelserna. Samma som i offerten. */
  contactName: z.string().nullable(),
  contactTitle: z.string().nullable(),
  contactPhone: z.string().nullable(),
  /** Vad formuläret frågar efter. */
  form: widgetFormSchema,
  /**
   * Falskt när byrån har angett domäner och sidan inte är en av dem. Då visar
   * formuläret att det inte är aktiverat här.
   */
  allowed: z.boolean(),
  /**
   * Den publika nyckeln för botkontrollen med Cloudflare Turnstile (ADR 0085).
   * Saknas den skickar formuläret utan kontroll, till exempel lokalt.
   */
  botCheckSiteKey: z.string().min(1).optional(),
});

export type WidgetConfig = z.infer<typeof widgetConfigSchema>;
